import { Controller } from "@hotwired/stimulus"

// Manages dynamic line items on the new/edit invoice form.
// Handles: adding blank item rows, removing rows, live total recalculation.
export default class extends Controller {
  static targets = ["items", "subtotal", "tax", "discount", "grandTotal", "itemCount", "customerSelect", "customerType", "unregisteredNotice"]

  connect() {
    this.recalculate()
    this._onProductSelected = () => this.recalculate()
    this.element.addEventListener("invoice-form:recalculate", this._onProductSelected)
  }

  disconnect() {
    this.element.removeEventListener("invoice-form:recalculate", this._onProductSelected)
  }

  // Add a new blank item row by cloning the template
  addItem(event) {
    event.preventDefault()
    const template = document.getElementById("invoice-item-template")
    if (!template) return

    const timestamp = new Date().getTime()
    const html = template.innerHTML.replace(/NEW_RECORD/g, timestamp)
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html

    const newRow = wrapper.firstElementChild
    const addBtn = event.currentTarget
    addBtn.parentElement.insertBefore(newRow, addBtn)

    // Smooth entrance
    newRow.style.opacity = "0"
    newRow.style.transform = "translateY(-8px)"
    requestAnimationFrame(() => {
      newRow.style.transition = "opacity 200ms ease, transform 200ms ease"
      newRow.style.opacity = "1"
      newRow.style.transform = "translateY(0)"
    })

    this.recalculate()
  }

  // Remove a line item row. For persisted records, mark _destroy and hide.
  // For new (unsaved) records, remove from DOM entirely.
  removeItem(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-item-row]")
    if (!row) return

    const destroyField = row.querySelector("[data-destroy-field]")

    if (destroyField) {
      // Persisted record - mark for destruction and hide with animation
      destroyField.value = "1"
      row.style.transition = "opacity 200ms ease, transform 200ms ease"
      row.style.opacity = "0"
      row.style.transform = "translateY(-8px)"
      setTimeout(() => { row.style.display = "none" }, 200)
    } else {
      // New record - remove from DOM with animation
      row.style.transition = "opacity 200ms ease, transform 200ms ease"
      row.style.opacity = "0"
      row.style.transform = "translateY(-8px)"
      setTimeout(() => row.remove(), 200)
    }

    setTimeout(() => this.recalculate(), 210)
  }

  // Update customer type hidden field when customer selection changes
  onCustomerChange(event) {
    const select = event.target
    const option = select.options[select.selectedIndex]
    const type   = option?.dataset?.customerType || ""
    if (this.hasCustomerTypeTarget) this.customerTypeTarget.value = type
    this.recalculate()
  }

  // Live recalculate totals from visible item rows
  recalculate() {
    let subtotal = 0
    let salesTaxTotal = 0
    let furtherTaxTotal = 0
    let discountTotal = 0

    const customerType = this.hasCustomerTypeTarget ? this.customerTypeTarget.value : ""
    const isUnregistered = customerType === "Unregistered"

    if (this.hasUnregisteredNoticeTarget) {
      this.unregisteredNoticeTarget.classList.toggle("hidden", !isUnregistered)
    }

    this.element.querySelectorAll("[data-item-row]").forEach(row => {
      if (row.style.display === "none") return

      const qty         = parseFloat(row.querySelector("[data-field='quantity']")?.value  || 0)
      const price       = parseFloat(row.querySelector("[data-field='unit_price']")?.value || 0)
      const tax         = parseFloat(row.querySelector("[data-field='tax_rate']")?.value   || 0)
      const disc        = parseFloat(row.querySelector("[data-field='discount']")?.value   || 0)
      const saleType    = row.querySelector("[data-field='sale_type']")?.value || ""
      const rateDesc    = row.querySelector("[data-product-search-target='rateDesc']")?.value || ""
      const retailPrice = parseFloat(row.querySelector("[data-product-search-target='retailPrice']")?.value || 0)

      const lineBase = qty * price
      const lineNet  = lineBase - disc   // matches backend net_amount

      // Mirror TaxCalculator.calculate_item sale-type logic
      const isStandard   = saleType === "Goods at standard rate (default)" || saleType === "Standard rate"
      const isExempt     = saleType === "Exempt goods"
      const isZeroRate   = saleType === "Goods at zero-rate" || saleType === "DTRE goods"
      const is3rdSched   = saleType === "3rd Schedule Goods"

      // Mirror RateParser: rate_desc is authoritative when present.
      //   "18%"                                  -> percent
      //   "Rs.200" / "Rs.700/MT"                 -> flat per-unit
      //   "18% along with rupees 60 per kilogram"-> mixed (percent + per-unit)
      //   "Exempt"                               -> zero
      const parsed = this.parseRateDesc(rateDesc, tax)

      let salesTax = 0
      if (isExempt || isZeroRate || parsed.exempt) {
        salesTax = 0
      } else if (is3rdSched) {
        // Tax on retail price × qty (not on net_amount)
        salesTax = retailPrice * (parsed.percent / 100) * qty
      } else if (parsed.kind === "per_unit") {
        salesTax = parsed.perUnit * qty
      } else if (parsed.kind === "mixed") {
        salesTax = lineNet * (parsed.percent / 100) + parsed.perUnit * qty
      } else {
        salesTax = lineNet * (parsed.percent / 100)
      }

      // 4% further tax for unregistered buyers at standard rate
      const furtherTax = (isUnregistered && isStandard) ? lineNet * 0.04 : 0
      const lineTax    = salesTax + furtherTax
      const lineTotal  = lineNet + lineTax

      subtotal        += lineNet    // matches backend: subtotal = Σ net_amount (post-discount)
      salesTaxTotal   += salesTax
      furtherTaxTotal += furtherTax
      discountTotal   += disc

      const totalEl = row.querySelector("[data-field='line_total']")
      if (totalEl) totalEl.textContent = "PKR " + this.fmt(lineTotal)

      const taxEl = row.querySelector("[data-field='line_tax_amount']")
      if (taxEl) {
        taxEl.innerHTML = lineTax > 0
          ? `Tax: PKR ${this.fmt(lineTax)}`
          : "&nbsp;"
      }
    })

    // Business-defined custom taxes, each charged on the base its definition
    // names. Mirrors TaxCalculator.custom_tax_bases / custom_tax_lines_for; they
    // never compound with each other.
    const fbrTax = salesTaxTotal + furtherTaxTotal
    const customTaxBases = {
      taxable_value:  subtotal,
      gross_value:    subtotal + discountTotal,
      sales_tax:      fbrTax,
      value_with_tax: subtotal + fbrTax
    }
    let customTaxTotal = 0
    this.element.querySelectorAll("[data-custom-tax-row]").forEach(row => {
      const rate   = parseFloat(row.dataset.rate || 0)
      const base   = customTaxBases[row.dataset.base] ?? subtotal
      const amount = base * (rate / 100)
      customTaxTotal += amount

      const amountEl = row.querySelector("[data-custom-tax-amount]")
      if (amountEl) amountEl.textContent = "PKR " + this.fmt(amount)
    })

    const grandTotal = subtotal + salesTaxTotal + furtherTaxTotal + customTaxTotal   // subtotal is already post-discount

    this.safeSet("invoice-subtotal",    "PKR " + this.fmt(subtotal))
    this.safeSet("invoice-tax",         "PKR " + this.fmt(salesTaxTotal))
    this.safeSet("invoice-further-tax", "PKR " + this.fmt(furtherTaxTotal))
    this.safeSet("invoice-grand",       "PKR " + this.fmt(grandTotal))

    const furtherRow = document.getElementById("invoice-further-tax-row")
    if (furtherRow) furtherRow.style.display = furtherTaxTotal > 0 ? "" : "none"

    // Show/hide discount row
    const discRow = document.getElementById("invoice-discount-row")
    if (discRow) {
      if (discountTotal > 0) {
        discRow.style.display = ""
        this.safeSet("invoice-discount", "- PKR " + this.fmt(discountTotal))
      } else {
        discRow.style.display = "none"
      }
    }

    this.updateItemCount()
  }

  // Wording matches the server-rendered badge ("3 added") so the label doesn't
  // rewrite itself the first time the user touches a field.
  updateItemCount() {
    const count = this.element.querySelectorAll("[data-item-row]").length -
                  this.element.querySelectorAll("[data-item-row][style*='display: none']").length
    const el = document.getElementById("item-count")
    if (el) el.textContent = `${count} added`
  }

  safeSet(id, value) {
    const el = document.getElementById(id)
    if (el) el.textContent = value
  }

  fmt(num) {
    return num.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Mirror app/services/rate_parser.rb so the live preview matches the saved invoice.
  parseRateDesc(rateDesc, fallbackPercent) {
    const str = (rateDesc || "").trim()
    if (!str) return { kind: "percent", percent: fallbackPercent || 0, perUnit: 0, exempt: false }

    const mixed = str.match(/^\s*([\d.]+)\s*%\s*along\s+with\s+rupees?\s+([\d.]+)/i)
    if (mixed) return { kind: "mixed", percent: parseFloat(mixed[1]), perUnit: parseFloat(mixed[2]), exempt: false }

    if (/^exempt$/i.test(str)) return { kind: "exempt", percent: 0, perUnit: 0, exempt: true }

    const pct = str.match(/^\s*([\d.]+)\s*%\s*$/)
    if (pct) return { kind: "percent", percent: parseFloat(pct[1]), perUnit: 0, exempt: false }

    const flat = str.match(/^\s*Rs\.?\s*([\d.]+)\s*(?:\/\s*\S+)?\s*$/i)
    if (flat) return { kind: "per_unit", percent: 0, perUnit: parseFloat(flat[1]), exempt: false }

    return { kind: "percent", percent: fallbackPercent || 0, perUnit: 0, exempt: false }
  }
}
